import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

// Registrar fuentes profesionales
Font.register({
  family: 'Montserrat',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCu170w-.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCuM70w-.ttf', fontWeight: 700 },
    { src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCvr70w-.ttf', fontWeight: 800 }
  ]
});

Font.register({
  family: 'Playfair',
  src: 'https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDXbtM.ttf'
});

Font.register({
  family: 'GreatVibes',
  src: 'https://fonts.gstatic.com/s/greatvibes/v14/RWmMoKWR9v4ksMfaWd_JN9XFiaQ.ttf'
});

// Colores del tema
const COLORS = {
  primary: '#0984e3',
  secondary: '#00b894',
  gold: '#d4af37',
  goldLight: '#f4e4bc',
  dark: '#2d3436',
  gray: '#636e72'
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    position: 'relative',
  },
  // Borde decorativo exterior
  outerBorder: {
    position: 'absolute',
    top: 15,
    left: 15,
    right: 15,
    bottom: 15,
    borderWidth: 3,
    borderColor: COLORS.gold,
    borderStyle: 'solid',
  },
  // Borde decorativo interior
  innerBorder: {
    position: 'absolute',
    top: 25,
    left: 25,
    right: 25,
    bottom: 25,
    borderWidth: 1,
    borderColor: COLORS.gold,
    borderStyle: 'solid',
  },
  // Esquinas decorativas
  cornerDecoration: {
    position: 'absolute',
    width: 60,
    height: 60,
  },
  topLeftCorner: {
    top: 20,
    left: 20,
  },
  topRightCorner: {
    top: 20,
    right: 20,
  },
  bottomLeftCorner: {
    bottom: 20,
    left: 20,
  },
  bottomRightCorner: {
    bottom: 20,
    right: 20,
  },
  // Contenido principal
  content: {
    flex: 1,
    paddingTop: 50,
    paddingBottom: 40,
    paddingHorizontal: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Logo y header
  headerSection: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 70,
    height: 70,
    marginBottom: 15,
  },
  companyName: {
    fontSize: 12,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    color: COLORS.gray,
    letterSpacing: 3,
    textTransform: 'uppercase',
  },
  // Título principal
  titleSection: {
    alignItems: 'center',
    marginBottom: 30,
  },
  certificateTitle: {
    fontSize: 36,
    fontFamily: 'Playfair',
    color: COLORS.gold,
    marginBottom: 5,
  },
  certificateSubtitle: {
    fontSize: 14,
    fontFamily: 'Montserrat',
    fontWeight: 400,
    color: COLORS.gray,
    letterSpacing: 4,
    textTransform: 'uppercase',
  },
  // Línea decorativa
  decorativeLine: {
    width: 150,
    height: 2,
    backgroundColor: COLORS.gold,
    marginVertical: 20,
  },
  decorativeLineShort: {
    width: 80,
    height: 1,
    backgroundColor: COLORS.gold,
    marginVertical: 10,
  },
  // Sección del nombre
  nameSection: {
    alignItems: 'center',
    marginBottom: 25,
  },
  presentedTo: {
    fontSize: 11,
    fontFamily: 'Montserrat',
    color: COLORS.gray,
    marginBottom: 10,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  recipientName: {
    fontSize: 42,
    fontFamily: 'GreatVibes',
    color: COLORS.dark,
    marginBottom: 5,
  },
  // Sección del curso
  courseSection: {
    alignItems: 'center',
    marginBottom: 30,
    paddingHorizontal: 40,
  },
  completionText: {
    fontSize: 11,
    fontFamily: 'Montserrat',
    color: COLORS.gray,
    textAlign: 'center',
    lineHeight: 1.6,
    marginBottom: 15,
  },
  courseName: {
    fontSize: 18,
    fontFamily: 'Montserrat',
    fontWeight: 700,
    color: COLORS.primary,
    textAlign: 'center',
    marginBottom: 10,
  },
  // Fecha
  dateSection: {
    alignItems: 'center',
    marginBottom: 30,
  },
  dateText: {
    fontSize: 11,
    fontFamily: 'Montserrat',
    color: COLORS.gray,
  },
  dateValue: {
    fontSize: 12,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    color: COLORS.dark,
    marginTop: 3,
  },
  // Sección de firmas
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingHorizontal: 60,
    marginTop: 20,
  },
  signatureBox: {
    alignItems: 'center',
    width: 180,
  },
  signatureLine: {
    width: 150,
    height: 1,
    backgroundColor: COLORS.dark,
    marginBottom: 8,
  },
  signatureName: {
    fontSize: 11,
    fontFamily: 'Montserrat',
    fontWeight: 600,
    color: COLORS.dark,
    marginBottom: 3,
  },
  signatureTitle: {
    fontSize: 9,
    fontFamily: 'Montserrat',
    color: COLORS.gray,
  },
  // Sello
  sealSection: {
    position: 'absolute',
    bottom: 80,
    right: 80,
    alignItems: 'center',
  },
  seal: {
    width: 80,
    height: 80,
    opacity: 0.8,
  },
  // ID del certificado
  certificateId: {
    position: 'absolute',
    bottom: 35,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  idText: {
    fontSize: 8,
    fontFamily: 'Montserrat',
    color: '#bdbdbd',
    letterSpacing: 1,
  },
  // Patrón de fondo sutil
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
  },
  // Ribbon/cinta decorativa
  ribbon: {
    position: 'absolute',
    top: 50,
    right: 50,
    width: 100,
    height: 100,
  },
});

// Componente de esquina decorativa SVG
const CornerDecoration = ({ style }) => (
  <View style={[styles.cornerDecoration, style]}>
    <Text style={{ fontSize: 30, color: COLORS.gold }}>❖</Text>
  </View>
);

const CertificateGenerator = ({ nombre, apellido, courseName, completionDate }) => {
  // Generar ID único para el certificado
  const certificateId = `CERT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
  
  // Formatear fecha
  const formattedDate = new Date(completionDate).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Bordes decorativos */}
        <View style={styles.outerBorder} />
        <View style={styles.innerBorder} />
        
        {/* Contenido principal */}
        <View style={styles.content}>
          {/* Header con logo */}
          <View style={styles.headerSection}>
            <Image style={styles.logo} src="/assets/logo.png" />
            <Text style={styles.companyName}>Formación Corporativa</Text>
          </View>

          {/* Título */}
          <View style={styles.titleSection}>
            <Text style={styles.certificateTitle}>Certificado</Text>
            <Text style={styles.certificateSubtitle}>de Finalización</Text>
          </View>

          <View style={styles.decorativeLine} />

          {/* Nombre del destinatario */}
          <View style={styles.nameSection}>
            <Text style={styles.presentedTo}>Se certifica que</Text>
            <Text style={styles.recipientName}>{nombre} {apellido}</Text>
          </View>

          <View style={styles.decorativeLineShort} />

          {/* Información del curso */}
          <View style={styles.courseSection}>
            <Text style={styles.completionText}>
              ha completado satisfactoriamente todas las lecciones y evaluaciones del curso
            </Text>
            <Text style={styles.courseName}>"{courseName}"</Text>
            <Text style={styles.completionText}>
              demostrando dedicación, compromiso y excelencia en el aprendizaje
            </Text>
          </View>

          {/* Fecha */}
          <View style={styles.dateSection}>
            <Text style={styles.dateText}>Fecha de emisión</Text>
            <Text style={styles.dateValue}>{formattedDate}</Text>
          </View>

          {/* Firmas */}
          <View style={styles.signatureSection}>
            <View style={styles.signatureBox}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>Director de Formación</Text>
              <Text style={styles.signatureTitle}>Responsable de Capacitación</Text>
            </View>
            
            <View style={styles.signatureBox}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureName}>Recursos Humanos</Text>
              <Text style={styles.signatureTitle}>Validación Corporativa</Text>
            </View>
          </View>
        </View>

        {/* ID del certificado */}
        <View style={styles.certificateId}>
          <Text style={styles.idText}>ID: {certificateId}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default CertificateGenerator;